import React, { useEffect, useState } from 'react';
import { Search, Filter, Download, UserPlus, Eye, CreditCard as Edit, Trash2, MoreHorizontal, AlertTriangle, CheckCircle, Clock, Banknote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { ProjectAssignmentModal } from '../components/Projects/ProjectAssignmentModal';
import { ProjectFunderAssignmentModal } from '../components/Projects/ProjectFunderAssignmentModal';
import { supabase } from '../lib/supabase';
import type { Project } from '../types';

interface ProjectOverview {
  id: string;
  name: string;
  status: string;
  domain: string;
  owner_name: string;
  owner_email: string;
  owner_organization: string;
  advisor_name: string;
  advisor_email: string;
  funder_name?: string;
  funder_email?: string;
  funding_requested: number;
  funding_amount: number;
  total_documents: number;
  verified_documents: number;
  pending_documents: number;
  document_completion_rate: number;
  total_steps: number;
  completed_steps: number;
  step_completion_rate: number;
  last_activity: string;
  created_at: string;
}

export const CoordinatorProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectOverview[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('');
  const [funderFilter, setFunderFilter] = useState('');
  const [selectedProjectForAssignment, setSelectedProjectForAssignment] = useState<Project | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [selectedProjectForFunderAssignment, setSelectedProjectForFunderAssignment] = useState<Project | null>(null);
  const [isFunderAssignmentModalOpen, setIsFunderAssignmentModalOpen] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter, domainFilter, advisorFilter, funderFilter]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('coordinator_project_overview')
        .select('*')
        .order('last_activity', { ascending: false });
      
      if (error) throw error;
      setProjects(data || []);
      console.log('✅ Projets chargés:', data?.length || 0);
    } catch (error) {
      console.error('Error loading projects:', error);
      // En cas d'erreur, essayer de charger depuis la table projects directement
      try {
        console.warn('Vue coordinator_project_overview non disponible, fallback vers table projects...');
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select(`
            *,
            owner:users!projects_owner_id_fkey(full_name, email, organization),
            assigned_advisor:users!projects_assigned_advisor_id_fkey(full_name, email),
            assigned_funder:users!projects_assigned_funder_id_fkey(full_name, email)
          `)
          .order('updated_at', { ascending: false });
        
        if (projectsError) throw projectsError;
        
        // Transform to match coordinator_project_overview structure
        const transformedProjects = (projectsData || []).map(p => ({
          id: p.id,
          name: p.name,
          status: p.status,
          domain: p.domain,
          owner_name: p.owner?.full_name || 'Nom non renseigné',
          owner_email: p.owner?.email || '',
          owner_organization: p.owner?.organization || '',
          advisor_name: p.assigned_advisor?.full_name || '',
          advisor_email: p.assigned_advisor?.email || '',
          funder_name: p.assigned_funder?.full_name || '',
          funder_email: p.assigned_funder?.email || '',
          funding_requested: p.funding_requested || 0,
          funding_amount: p.funding_amount || 0,
          total_documents: 0,
          verified_documents: 0,
          pending_documents: 0,
          document_completion_rate: 0,
          total_steps: 0,
          completed_steps: 0,
          step_completion_rate: 0,
          last_activity: p.updated_at,
          created_at: p.created_at
        }));
        
        setProjects(transformedProjects);
        console.log('✅ Projets chargés via fallback:', transformedProjects.length);
      } catch (fallbackError) {
        console.error('Erreur fallback:', fallbackError);
        setProjects([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.advisor_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    if (domainFilter) {
      filtered = filtered.filter(project => project.domain === domainFilter);
    }

    if (advisorFilter === 'unassigned') {
      filtered = filtered.filter(project => !project.advisor_name);
    } else if (advisorFilter === 'assigned') {
      filtered = filtered.filter(project => project.advisor_name);
    }

    if (funderFilter === 'unassigned') {
      filtered = filtered.filter(project => !project.funder_name);
    } else if (funderFilter === 'assigned') {
      filtered = filtered.filter(project => project.funder_name);
    }

    setFilteredProjects(filtered);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800',
      SOUMIS: 'bg-blue-100 text-blue-800',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800',
      VALIDE: 'bg-green-100 text-green-800',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
      EN_COURS: 'bg-indigo-100 text-indigo-800',
      VERIFICATION: 'bg-orange-100 text-orange-800',
      TERMINE: 'bg-emerald-100 text-emerald-800',
      REJETE: 'bg-red-100 text-red-800',
      SUSPENDU: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getDomainLabel = (domain: string) => {
    const labels = {
      AGRICULTURE: 'Agriculture',
      ARTISANAT: 'Artisanat',
      COMMERCE: 'Commerce',
      SERVICES: 'Services',
      INDUSTRIE: 'Industrie',
      TOURISME: 'Tourisme',
      CULTURE: 'Culture',
      FORMATION: 'Formation',
      SOCIAL: 'Social',
      ENVIRONNEMENT: 'Environnement'
    };
    return labels[domain as keyof typeof labels] || domain;
  };

  const getCompletionIcon = (rate: number) => {
    if (rate === 100) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (rate > 0) return <Clock className="w-4 h-4 text-orange-600" />;
    return <AlertTriangle className="w-4 h-4 text-red-600" />;
  };

  const handleAssignAdvisor = (project: ProjectOverview) => {
    const projectData: Project = {
      id: project.id,
      name: project.name,
      description: null,
      status: project.status as any,
      domain: project.domain as any,
      owner_id: '',
      assigned_advisor_id: null,
      created_at: project.created_at,
      updated_at: project.last_activity,
      owner: {
        id: '',
        full_name: project.owner_name,
        email: project.owner_email,
        profile_type: 'PORTEUR',
        phone: null,
        organization: project.owner_organization,
        address: null,
        city: null,
        postal_code: null,
        created_at: '',
        updated_at: ''
      }
    };
    setSelectedProjectForAssignment(projectData);
    setIsAssignmentModalOpen(true);
  };

  const handleAssignFunder = (project: ProjectOverview) => {
    const projectData: Project = {
      id: project.id,
      name: project.name,
      description: null,
      status: project.status as any,
      domain: project.domain as any,
      owner_id: '',
      assigned_advisor_id: null,
      assigned_funder_id: null,
      created_at: project.created_at,
      updated_at: project.last_activity,
      owner: {
        id: '',
        full_name: project.owner_name,
        email: project.owner_email,
        profile_type: 'PORTEUR',
        phone: null,
        organization: project.owner_organization,
        address: null,
        city: null,
        postal_code: null,
        created_at: '',
        updated_at: ''
      },
      assigned_funder: project.funder_name ? {
        id: '',
        full_name: project.funder_name,
        email: project.funder_email || '',
        profile_type: 'FINANCEUR',
        phone: null,
        organization: null,
        address: null,
        city: null,
        postal_code: null,
        created_at: '',
        updated_at: ''
      } : undefined
    };
    setSelectedProjectForFunderAssignment(projectData);
    setIsFunderAssignmentModalOpen(true);
  };

  const handleAssignmentSuccess = () => {
    setIsAssignmentModalOpen(false);
    setSelectedProjectForAssignment(null);
    loadProjects(); // Refresh the projects list
  };

  const handleFunderAssignmentSuccess = () => {
    setIsFunderAssignmentModalOpen(false);
    setSelectedProjectForFunderAssignment(null);
    loadProjects(); // Refresh the projects list
  };

  const handleDeleteProject = async (project: ProjectOverview) => {
    const confirmMessage = `Êtes-vous sûr de vouloir supprimer définitivement le projet "${project.name}" ?\n\nCette action supprimera :\n- Le projet\n- Tous les documents associés\n- Toutes les étapes\n- Tous les rendez-vous\n- Toutes les notifications\n- Tout l'historique\n\nCette action est IRRÉVERSIBLE.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete project (cascade will handle related data)
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', project.id);

      if (error) throw error;

      // Remove project from local state immediately
      setProjects(prev => prev.filter(p => p.id !== project.id));
      setFilteredProjects(prev => prev.filter(p => p.id !== project.id));
      
      // Show success message
      alert(`Le projet "${project.name}" a été supprimé avec succès.`);
    } catch (error: any) {
      console.error('Error deleting project:', error);
      alert(`Erreur lors de la suppression : ${error.message}`);
    }
  };
  const handleExportProjects = () => {
    // TODO: Implement CSV export
    console.log('Export projects');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tous les projets</h1>
            <p className="text-gray-600 mt-1">
              Gestion et supervision de l'ensemble des projets ESS
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportProjects}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Exporter</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="A_PREPARER">À préparer</option>
              <option value="SOUMIS">Soumis</option>
              <option value="ACCORD_PROVISOIRE">Accord provisoire</option>
              <option value="VALIDE">Validé</option>
              <option value="DEBLOCAGE_FONDS">Déblocage fonds</option>
              <option value="EN_COURS">En cours</option>
              <option value="VERIFICATION">Vérification</option>
              <option value="TERMINE">Terminé</option>
              <option value="REJETE">Rejeté</option>
              <option value="SUSPENDU">Suspendu</option>
            </select>

            {/* Domain Filter */}
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les secteurs</option>
              <option value="AGRICULTURE">Agriculture</option>
              <option value="ARTISANAT">Artisanat</option>
              <option value="COMMERCE">Commerce</option>
              <option value="SERVICES">Services</option>
              <option value="INDUSTRIE">Industrie</option>
              <option value="TOURISME">Tourisme</option>
              <option value="CULTURE">Culture</option>
              <option value="FORMATION">Formation</option>
              <option value="SOCIAL">Social</option>
              <option value="ENVIRONNEMENT">Environnement</option>
            </select>

            {/* Advisor Filter */}
            <select
              value={advisorFilter}
              onChange={(e) => setAdvisorFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les accompagnateurs</option>
              <option value="assigned">Avec accompagnateur</option>
              <option value="unassigned">Sans accompagnateur</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
            {/* Funder Filter */}
            <select
              value={funderFilter}
              onChange={(e) => setFunderFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous les financeurs</option>
              <option value="assigned">Avec financeur</option>
              <option value="unassigned">Sans financeur</option>
            </select>
          </div>
        </div>

        {/* Projects Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Porteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Accompagnateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Financeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progression
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Financement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">{project.name}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                            {getStatusLabel(project.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{getDomainLabel(project.domain)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{project.owner_name}</p>
                        <p className="text-sm text-gray-500">{project.owner_organization}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {project.advisor_name ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">{project.advisor_name}</p>
                          <p className="text-sm text-gray-500">{project.advisor_email}</p>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAssignAdvisor(project)}
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>Assigner</span>
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {project.funder_name ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">{project.funder_name}</p>
                          <p className="text-sm text-gray-500">{project.funder_email}</p>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAssignFunder(project)}
                          className="flex items-center space-x-1 text-green-600 hover:text-green-700 text-sm"
                        >
                          <Banknote className="w-4 h-4" />
                          <span>Assigner</span>
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getCompletionIcon(project.document_completion_rate)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {Math.round(project.document_completion_rate)}%
                          </p>
                          <p className="text-xs text-gray-500">
                            {project.verified_documents}/{project.total_documents} vérifiés
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${project.step_completion_rate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {Math.round(project.step_completion_rate)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        {project.funding_amount > 0 ? (
                          <>
                            <p className="text-sm font-medium text-green-600">
                              {project.funding_amount.toLocaleString('fr-FR')} €
                            </p>
                            <p className="text-xs text-gray-500">
                              sur {project.funding_requested.toLocaleString('fr-FR')} €
                            </p>
                          </>
                        ) : project.funding_requested > 0 ? (
                          <p className="text-sm text-gray-600">
                            {project.funding_requested.toLocaleString('fr-FR')} € demandés
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">Non renseigné</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Voir le projet"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/projects/${project.id}/edit`)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Modifier le projet"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Supprimer le projet"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucun projet trouvé avec les filtres sélectionnés.</p>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{filteredProjects.length}</p>
              <p className="text-sm text-gray-600">Projets affichés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {filteredProjects.filter(p => p.status === 'TERMINE').length}
              </p>
              <p className="text-sm text-gray-600">Terminés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {filteredProjects.filter(p => ['EN_COURS', 'VALIDE', 'DEBLOCAGE_FONDS'].includes(p.status)).length}
              </p>
              <p className="text-sm text-gray-600">En cours</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {filteredProjects.filter(p => !p.advisor_name).length}
              </p>
              <p className="text-sm text-gray-600">Sans accompagnateur</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {filteredProjects.filter(p => !p.funder_name).length}
              </p>
              <p className="text-sm text-gray-600">Sans financeur</p>
            </div>
          </div>
        </div>

        {/* Assignment Modal */}
        {selectedProjectForAssignment && (
          <ProjectAssignmentModal
            isOpen={isAssignmentModalOpen}
            onClose={() => {
              setIsAssignmentModalOpen(false);
              setSelectedProjectForAssignment(null);
            }}
            project={selectedProjectForAssignment}
            onSuccess={handleAssignmentSuccess}
          />
        )}

        {/* Funder Assignment Modal */}
        {selectedProjectForFunderAssignment && (
          <ProjectFunderAssignmentModal
            isOpen={isFunderAssignmentModalOpen}
            onClose={() => {
              setIsFunderAssignmentModalOpen(false);
              setSelectedProjectForFunderAssignment(null);
            }}
            project={selectedProjectForFunderAssignment}
            onSuccess={handleFunderAssignmentSuccess}
          />
        )}
      </div>
    </Layout>
  );
};